/**
 * DeliveryGlyph — the one rung of the delivery ladder a user bubble shows
 * (2026-09-08, chat-thread experience PR-2): ○ sending · ⧗ offline · ✓ sent ·
 * ✓✓ delivered · ✓✓ seen (accent) · ✓! sent-without-its-file · ! not sent.
 * Sits in the nowrap
 * MessageTime row next to the timestamp; the full word is the tooltip and the
 * accessible label. Pure presentational — the status is derived by the host
 * from the delivery ledger, never stored on the message.
 */
import React from 'react';
import { DELIVERY_STATUS_COPY, type DeliveryStatus } from '../../services/messageDelivery/deliveryStatus';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';

const COLOR: Record<DeliveryStatus, string> = {
  pending: 'rgba(127,127,127,0.55)',
  'queued-offline': 'rgba(127,127,127,0.75)',
  sent: 'rgba(127,127,127,0.85)',
  delivered: 'rgba(127,127,127,0.85)',
  read: '#8a6d2f',
  // Amber, not the failure red: half of this message DID arrive, and painting
  // it the same colour as "not sent" would overstate the loss (2026-09-11).
  'attachments-missing': '#b54708',
  failed: '#b42318',
};

export interface DeliveryGlyphProps {
  status: DeliveryStatus;
  /** Optional detail for the tooltip (a failure reason, a seen-by list). */
  detail?: string;
  /**
   * Send the files again (2026-09-11). Present only where the offer is real —
   * an 'attachments-missing' message whose original File objects are still in
   * memory. Without it the glyph is a dead end that names a problem and gives
   * no way out, which is worse than not naming it.
   */
  onRetry?: () => void;
  /** True while a retry is in flight; the control says so and cannot re-fire. */
  retrying?: boolean;
}

export function DeliveryGlyph({ status, detail, onRetry, retrying }: DeliveryGlyphProps): React.ReactElement {
  const copy = DELIVERY_STATUS_COPY[status];
  const es = getUserUiLanguage() === 'es';
  const label = es ? copy.es : copy.label;
  const title = detail ? `${label} — ${detail}` : label;

  // The offer, where one exists: the glyph stays a glyph and the action sits
  // beside it as a real button, so it is reachable by keyboard and reads to a
  // screen reader as an action rather than as decoration.
  if (onRetry && status === 'attachments-missing') {
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <span
          role="img"
          aria-label={title}
          title={title}
          data-delivery-status={status}
          style={{ marginLeft: 6, fontSize: '0.7rem', color: COLOR[status], fontWeight: 700 }}
        >
          {copy.glyph}
        </span>
        <button
          type="button"
          data-delivery-retry=""
          onClick={onRetry}
          disabled={retrying === true}
          title={title}
          style={{
            marginLeft: 4,
            padding: 0,
            border: 'none',
            background: 'none',
            font: 'inherit',
            fontSize: '0.7rem',
            color: COLOR[status],
            textDecoration: 'underline',
            cursor: retrying ? 'default' : 'pointer',
            opacity: retrying ? 0.6 : 1,
          }}
        >
          {retrying ? (es ? 'reintentando…' : 'retrying…') : (es ? 'reintentar' : 'retry')}
        </button>
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label={title}
      title={title}
      data-delivery-status={status}
      style={{
        marginLeft: 6,
        fontSize: '0.7rem',
        letterSpacing: status === 'delivered' || status === 'read' ? '-0.15em' : undefined,
        color: COLOR[status],
        fontWeight: status === 'failed' || status === 'attachments-missing' ? 700 : 500,
        whiteSpace: 'nowrap',
      }}
    >
      {copy.glyph}
    </span>
  );
}

export default DeliveryGlyph;
